import { useInfiniteQuery } from "@tanstack/react-query";
import {
  type DocumentData,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  type QueryDocumentSnapshot,
  query,
  startAfter,
} from "firebase/firestore";
import { type FC, useMemo } from "react";
import { ImArrowDown, ImArrowUp } from "react-icons/im";
import { queryKeys } from "../../../queries/queryKeys";
import {
  crearMovimientosRutaCollectionRef,
  type MovimientoRutaCompatible,
  obtenerTipoMovimientoRuta,
} from "../../../utils/rutaMovimientos";
import type { TablaMovimientosProps } from "../types";

const MOVIMIENTOS_POR_PAGINA = 50;

type MovimientoTabla = MovimientoRutaCompatible & {
  id: string;
};

type MovimientosPagina = {
  items: MovimientoTabla[];
  nextCursor: QueryDocumentSnapshot<DocumentData> | null;
};

const fetchMovimientosPagina = async (
  adminUid: string,
  rutaUid: string,
  cursor: QueryDocumentSnapshot<DocumentData> | null,
): Promise<MovimientosPagina> => {
  const db = getFirestore();
  const movimientosRef = crearMovimientosRutaCollectionRef(db, adminUid, rutaUid);
  const movimientosQuery = cursor
    ? query(
        movimientosRef,
        orderBy("fecha", "desc"),
        startAfter(cursor),
        limit(MOVIMIENTOS_POR_PAGINA),
      )
    : query(movimientosRef, orderBy("fecha", "desc"), limit(MOVIMIENTOS_POR_PAGINA));
  const movimientosSnapshot = await getDocs(movimientosQuery);
  const items = movimientosSnapshot.docs.map((movimientoDoc) => ({
    id: movimientoDoc.id,
    ...(movimientoDoc.data() as MovimientoRutaCompatible),
  }));

  return {
    items,
    nextCursor:
      movimientosSnapshot.docs.length === MOVIMIENTOS_POR_PAGINA
        ? (movimientosSnapshot.docs[movimientosSnapshot.docs.length - 1] ?? null)
        : null,
  };
};

export const TablaMovimientos: FC<TablaMovimientosProps> = ({ usuarioRuta, formatDate }) => {
  const adminUid = usuarioRuta?.adminUid;
  const rutaUid = usuarioRuta?.uid;

  // Consulta ocasional (no se mira constantemente); staleTime moderado evita
  // repetir el fetch al reabrir esta vista para la misma ruta, y el gcTime
  // acotado evita acumular paginas sin techo en sesiones de 8+ horas.
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    enabled: Boolean(adminUid && rutaUid),
    gcTime: 10 * 60 * 1000,
    getNextPageParam: (lastPage: MovimientosPagina) => lastPage.nextCursor,
    initialPageParam: null as QueryDocumentSnapshot<DocumentData> | null,
    queryFn: ({ pageParam }) =>
      fetchMovimientosPagina(adminUid as string, rutaUid as string, pageParam),
    queryKey: queryKeys.movimientos(adminUid, rutaUid),
    staleTime: 90 * 1000,
  });

  const movimientos = useMemo(
    () => data?.pages.flatMap((pagina: MovimientosPagina) => pagina.items) ?? [],
    [data],
  );
  const hasMore = Boolean(hasNextPage);

  const claseColores = (movimiento: MovimientoRutaCompatible) => {
    return obtenerTipoMovimientoRuta(movimiento.descripcion ?? "", movimiento.tipo) === "ingreso"
      ? "text-green-700"
      : "text-red-600";
  };

  const esMovimientoIngreso = (movimiento: MovimientoRutaCompatible): boolean =>
    obtenerTipoMovimientoRuta(movimiento.descripcion ?? "", movimiento.tipo) === "ingreso";

  return (
    <>
      <table className="min-w-full mt-2 bg-white">
        <thead>
          <tr className="bg-[#8131bd] text-white uppercase text-sm">
            <th className="whitespace-nowrap border border-black w-[8%] px-2 py-1">#</th>
            <th className="whitespace-nowrap border border-black w-[46%] px-2 py-1">Monto</th>
            <th className="whitespace-nowrap border border-black w-[46%] px-2 py-1">Fecha</th>
            <th className="whitespace-nowrap border border-black w-[46%] px-2 py-1">Motivo</th>
            <th className="whitespace-nowrap border border-black w-[46%] px-2 py-1">Usuario</th>
          </tr>
        </thead>
        <tbody>
          {movimientos.length > 0 ? (
            movimientos.map((item, index) => (
              <tr
                key={item.id}
                className={`${
                  index % 2 === 0 ? "bg-gray-300" : "bg-gray-100"
                } border border-black font-semibold uppercase text-sm`}
              >
                <td className="whitespace-nowrap border border-black text-center px-2 py-1">
                  {index + 1}
                </td>
                <td
                  className={`whitespace-nowrap border border-black text-center px-2 py-1 ${claseColores(
                    item,
                  )} `}
                >
                  <div className="whitespace-nowrap items-center w-full h-full flex gap-2 mx-auto justify-center">
                    {esMovimientoIngreso(item) ? <ImArrowDown /> : <ImArrowUp />}
                    <span>R${item.monto ?? 0}</span>
                  </div>
                </td>
                <td className="items-center whitespace-nowrap border-black flex justify-center px-1 py-1">
                  {item.fecha ? formatDate(item.fecha) : "Sin fecha"}
                </td>
                <td className="border border-black text-center px-2 py-1 whitespace-nowrap">
                  {item.descripcion ?? "Sin datos"}
                </td>
                <td className="border border-black text-center px-2 py-1 whitespace-nowrap">
                  {item.responsable ?? "Sin datos"}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={8} className="border border-black px-2 py-2 text-center">
                {isLoading ? "Cargando movimientos..." : "No hay datos disponibles."}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {hasMore ? (
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            className="bg-[#8131bd] text-white px-3 py-1 rounded-md min-w-[120px]"
            disabled={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
          >
            {isFetchingNextPage ? "Cargando..." : "Cargar mas"}
          </button>
        </div>
      ) : null}
    </>
  );
};
